import React, { useEffect, useRef } from 'react';
import { Button, DatePicker, Form, Input, InputNumber, Space, Select } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetPOAByIdQuery, useUpdatePOAMutation, useLazyExportPOAQuery, type POA, type POAItem } from '../../store/api/poaApi';
import dayjs from 'dayjs';
import { useGetDriversQuery, useGetAllContractorsQuery } from '../../store/api/referencesApi';
import { DataTable } from '../../ui';

function poaToFormValues(poa: POA) {
  return {
    number: poa.number,
    issueDate: poa.issueDate ? dayjs(poa.issueDate) : undefined,
    validUntil: poa.validUntil ? dayjs(poa.validUntil) : undefined,
    validityDays: poa.validityDays,
    organizationName: poa.organizationName,
    supplierName: poa.supplierName,
    supplierInn: poa.supplierInn,
    supplierKpp: poa.supplierKpp,
    supplierAddress: poa.supplierAddress,
    employeeFullName: poa.employeeFullName,
    employeePosition: poa.employeePosition,
    employeePassportSeries: poa.employeePassportSeries,
    employeePassportNumber: poa.employeePassportNumber,
    employeeIssuedBy: poa.employeeIssuedBy,
    employeeIssuedAt: poa.employeeIssuedAt ? dayjs(poa.employeeIssuedAt) : undefined,
  };
}

const POAEditPage: React.FC = () => {
  const { id } = useParams();
  const poaId = Number(id);
  const { data: poa } = useGetPOAByIdQuery(poaId);
  const [updatePOA] = useUpdatePOAMutation();
  const [triggerExport] = useLazyExportPOAQuery();
  const [form] = Form.useForm();
  const saveTimer = useRef<number | null>(null);
  const hydratedPoaIdRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const { data: drivers = [] } = useGetDriversQuery(undefined);
  const { data: contractors = [] } = useGetAllContractorsQuery(undefined);

  useEffect(() => {
    hydratedPoaIdRef.current = null;
  }, [poaId]);

  useEffect(() => {
    if (!poa) return;
    if (hydratedPoaIdRef.current === poaId) return;
    hydratedPoaIdRef.current = poaId;
    form.setFieldsValue(poaToFormValues(poa));
  }, [poa, poaId, form]);

  const scheduleSave = () => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      const values = form.getFieldsValue(true);

      const formatDateForSave = (dateValue: any): string | null => {
        if (!dateValue) return null;
        return dateValue.format?.('YYYY-MM-DD') ?? null;
      };

      const payload: Record<string, unknown> = {
        ...values,
        issueDate: formatDateForSave(values.issueDate),
        validUntil: formatDateForSave(values.validUntil),
        employeeIssuedAt: formatDateForSave(values.employeeIssuedAt),
      };
      if (values.validityDays === '' || values.validityDays === undefined || values.validityDays === null) {
        payload.validityDays = null;
        payload.validUntil = null;
      }
      if (values.validUntil === null) payload.validUntil = null;
      if (values.issueDate === null) payload.issueDate = null;
      if (values.employeeIssuedAt === null) payload.employeeIssuedAt = null;

      try {
        await updatePOA({ id: poaId, data: payload }).unwrap();
      } catch {
        /* autosave — silent; user keeps editing */
      }
    }, 600);
  };

  const parsePassport = (line?: string | null) => {
    const result: { series?: string; number?: string; issuedBy?: string; issuedAt?: string } = {};
    if (!line) return result;
    const text = String(line).trim();
    const m = text.match(/^(\d{2}\s?\d{2})\s+(\d{6})(?:[,;]?\s*)(.*)$/);
    if (m) {
      result.series = m[1].replace(/\s+/g, ' ');
      result.number = m[2];
      const tail = m[3] || '';
      const d = tail.match(/(\d{2}\.\d{2}\.\d{4})/);
      if (d) {
        result.issuedAt = d[1];
        result.issuedBy = tail.replace(d[0], '').replace(/[ ,]+$/,'').replace(/^,\s*/,'').trim();
      } else {
        result.issuedBy = tail.trim();
      }
    }
    return result;
  };

  const handleSelectDriver = (driverId: number) => {
    const d = drivers.find((x: { id: number }) => x.id === driverId);
    if (!d) return;
    const parsed = parsePassport(d.passportData);
    form.setFieldsValue({
      employeeFullName: d.fullName,
      employeePosition: form.getFieldValue('employeePosition') || 'водитель',
      employeePassportSeries: parsed.series,
      employeePassportNumber: parsed.number,
      employeeIssuedBy: parsed.issuedBy,
      employeeIssuedAt: parsed.issuedAt ? dayjs(parsed.issuedAt, 'DD.MM.YYYY') : undefined,
    });
    scheduleSave();
  };

  const handleSelectSupplier = (contractorId: number) => {
    const c = contractors.find((x: { id: number }) => x.id === contractorId);
    if (!c) return;
    form.setFieldsValue({
      supplierId: c.id,
      supplierName: c.name,
      supplierInn: (c as { inn?: string }).inn || undefined,
      supplierAddress: c.address || undefined,
    });
    scheduleSave();
  };

  const handleExport = async () => {
    const blob = await triggerExport(poaId).unwrap();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Доверенность ${poa?.number || poaId}.xlsx`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };

  const recalcValidUntil = () => {
    const vals = form.getFieldsValue();
    const issue = vals.issueDate;
    const raw = vals.validityDays;
    if (raw === '' || raw === undefined || raw === null) {
      form.setFieldsValue({ validUntil: undefined });
      scheduleSave();
      return;
    }
    const days = Number(raw);
    if (issue && Number.isFinite(days) && days > 0) {
      const newDate = issue.add(days, 'day');
      form.setFieldsValue({ validUntil: newDate });
      scheduleSave();
    } else {
      form.setFieldsValue({ validUntil: undefined });
      scheduleSave();
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button onClick={() => navigate('/poa')}>К списку</Button>
        <Button type="primary" onClick={handleExport}>Экспорт в Excel</Button>
      </Space>

      <Form form={form} layout="vertical" onValuesChange={scheduleSave}>
        <Space wrap size={24}>
          <Form.Item label="Организация" name="organizationName"><Input placeholder='ООО "ЮМИГА"' /></Form.Item>
          <Form.Item label="Номер" name="number"><Input style={{ width: 160 }} /></Form.Item>
          <Form.Item label="Дата выдачи" name="issueDate"><DatePicker format="DD.MM.YYYY" onChange={recalcValidUntil} /></Form.Item>
          <Form.Item label="Действительна по" name="validUntil"><DatePicker format="DD.MM.YYYY" onChange={() => {
            const v = form.getFieldValue('validUntil');
            if (!v) {
              form.setFieldsValue({ validityDays: undefined });
              scheduleSave();
            }
          }} /></Form.Item>
          <Form.Item label="на (дней)" name="validityDays"><InputNumber min={0} onChange={recalcValidUntil} placeholder="—" /></Form.Item>
        </Space>

        <Space direction="vertical" size={0} style={{ display: 'block', marginTop: 16 }}>
          <h3>Поставщик (на получение от)</h3>
          <Space wrap size={24}>
            <Form.Item label="Выбрать из справочника">
              <Select
                showSearch
                placeholder="Найти контрагента"
                style={{ minWidth: 300 }}
                options={contractors.map((c: { id: number; name: string }) => ({ value: c.id, label: c.name }))}
                onChange={(val) => handleSelectSupplier(val)}
                filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
            <Form.Item label="Наименование" name="supplierName"><Input style={{ minWidth: 400 }} /></Form.Item>
            <Form.Item label="ИНН" name="supplierInn"><Input style={{ width: 160 }} /></Form.Item>
            <Form.Item label="КПП" name="supplierKpp"><Input style={{ width: 160 }} /></Form.Item>
            <Form.Item label="Адрес" name="supplierAddress"><Input style={{ minWidth: 400 }} /></Form.Item>
          </Space>
        </Space>

        <Space direction="vertical" size={0} style={{ display: 'block', marginTop: 16 }}>
          <h3>Выдана</h3>
          <Space wrap size={24}>
            <Form.Item label="Выбрать водителя">
              <Select
                showSearch
                placeholder="Найти водителя"
                style={{ minWidth: 300 }}
                options={drivers.map((d: { id: number; fullName: string }) => ({ value: d.id, label: d.fullName }))}
                onChange={(val) => handleSelectDriver(val)}
                filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
            <Form.Item label="ФИО" name="employeeFullName"><Input style={{ minWidth: 360 }} /></Form.Item>
            <Form.Item label="Должность" name="employeePosition"><Input style={{ width: 240 }} /></Form.Item>
            <Form.Item label="Паспорт серия" name="employeePassportSeries"><Input style={{ width: 120 }} /></Form.Item>
            <Form.Item label="Паспорт номер" name="employeePassportNumber"><Input style={{ width: 160 }} /></Form.Item>
            <Form.Item label="Кем выдан" name="employeeIssuedBy"><Input style={{ minWidth: 400 }} /></Form.Item>
            <Form.Item label="Дата выдачи" name="employeeIssuedAt"><DatePicker format="DD.MM.YYYY" /></Form.Item>
          </Space>
        </Space>

        <div style={{ marginTop: 24 }}>
          <h3>Перечень ТМЦ</h3>
          <DataTable
            rowKey={(r, index) => String((r as POAItem).id ?? `${(r as POAItem).name}-${index}`)}
            dataSource={poa?.items || []}
            pagination={false}
            columns={[
              { title: 'Наименование', dataIndex: 'name' },
              { title: 'Ед. изм.', dataIndex: 'unit' },
              { title: 'Количество', dataIndex: 'quantity' },
              { title: 'Группа', dataIndex: 'group' },
            ]}
          />
        </div>
      </Form>
    </div>
  );
};

export default POAEditPage;
